/**
 * CostTableEditor.tsx
 *
 * Spreadsheet-like editor for a single CostTableConfig.
 *
 * Features:
 *  - Editable table title
 *  - Columns: Service, Provider, Base Price, Margin %, Client Price (read-only, auto-calc)
 *  - "+ Add row" button
 *  - Row delete button ("x")
 *  - Auto-calculated total row
 *  - "Delete table" danger button
 *
 * All calculations happen inline on every change and the updated table is
 * passed up via `onUpdate`. The component is fully controlled — it owns no
 * internal copy of the table data.
 */

import type { ChangeEvent } from 'react';

import type { CostTableConfig, CostRow } from '@/types/wizardV2';
import styles from './CostTableEditor.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CostTableEditorProps {
  /**
   * The cost table being edited.
   * The component is fully controlled — callers own the source of truth.
   */
  table: CostTableConfig;

  /**
   * Called whenever any value changes (title, row field, add/delete).
   * Receives a fully recalculated copy of the table.
   */
  onUpdate: (table: CostTableConfig) => void;

  /**
   * Optional provider list for the provider column dropdown.
   * When provided, the "Provider" cell renders a `<select>` instead of
   * a plain text input.
   */
  providers?: Array<{ id: string; name: string }>;

  /**
   * Called when the user clicks "Delete table".
   * The parent is responsible for removing this table from the store.
   */
  onDelete: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Recalculate `clientPrice` for every row and the `total` field.
 * Returns a new table object — never mutates the input.
 *
 * @param table - Source table to recalculate.
 * @returns A new CostTableConfig with up-to-date derived values.
 */
function recalculate(table: CostTableConfig): CostTableConfig {
  const rows: CostRow[] = table.rows.map((row) => {
    const clientPrice = row.basePrice * (1 + row.marginPercent / 100);
    return { ...row, clientPrice: Math.round(clientPrice * 100) / 100 };
  });
  const total = rows.reduce((sum, r) => sum + r.clientPrice, 0);
  return { ...table, rows, total: Math.round(total * 100) / 100 };
}

/**
 * Create a fresh empty row with sensible defaults.
 *
 * @returns A new CostRow with empty strings and a default 15% margin.
 */
function emptyRow(): CostRow {
  return {
    service: '',
    provider: '',
    basePrice: 0,
    marginPercent: 15,
    clientPrice: 0,
  };
}

/**
 * Format a number as a currency string (S/ prefix for Peruvian sol).
 * Uses `toLocaleString` for thousands separators and 2 decimal places.
 *
 * @param value - Numeric value to format.
 * @returns Formatted currency string.
 */
function formatCurrency(value: number | undefined | null): string {
  const num = Number(value ?? 0);
  return `S/ ${num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CostTableEditor — fully controlled spreadsheet editor for one cost table.
 *
 * The component never holds local state for the table data itself.
 * All mutations are transformed into a `recalculate()`d copy and forwarded
 * to the parent via `onUpdate`.
 */
export const CostTableEditor = ({
  table,
  onUpdate,
  providers,
  onDelete,
}: CostTableEditorProps) => {
  // ── Handlers ───────────────────────────────────────────────────────────────

  /**
   * Update the table title.
   */
  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onUpdate(recalculate({ ...table, title: e.target.value }));
  };

  /**
   * Update a single field on the row at `rowIndex`.
   * Recalculates after every change so client prices stay in sync.
   *
   * @param rowIndex - Zero-based index of the row to update.
   * @param field - Key of the CostRow field being changed.
   * @param value - New string value from the input element.
   */
  const handleRowChange = (
    rowIndex: number,
    field: keyof CostRow,
    value: string,
  ) => {
    const updatedRows = table.rows.map((row, i) => {
      if (i !== rowIndex) return row;
      const numericFields: Array<keyof CostRow> = ['basePrice', 'marginPercent', 'clientPrice'];
      if (numericFields.includes(field)) {
        return { ...row, [field]: parseFloat(value) || 0 };
      }
      return { ...row, [field]: value };
    });
    onUpdate(recalculate({ ...table, rows: updatedRows }));
  };

  /**
   * Append an empty row with default values.
   */
  const handleAddRow = () => {
    onUpdate(recalculate({ ...table, rows: [...table.rows, emptyRow()] }));
  };

  /**
   * Remove the row at `rowIndex`.
   *
   * @param rowIndex - Zero-based index of the row to remove.
   */
  const handleDeleteRow = (rowIndex: number) => {
    const updatedRows = table.rows.filter((_, i) => i !== rowIndex);
    onUpdate(recalculate({ ...table, rows: updatedRows }));
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.editor}>
      {/* ── Table title ─────────────────────────────────────────────────── */}
      <div className={styles.titleRow}>
        <input
          type="text"
          className={styles.titleInput}
          value={table.title}
          onChange={handleTitleChange}
          placeholder="Table title"
          aria-label="Cost table title"
        />
        <button
          type="button"
          className={styles.deleteTableButton}
          onClick={onDelete}
          aria-label="Delete this cost table"
        >
          Delete table
        </button>
      </div>

      {/* ── Spreadsheet ─────────────────────────────────────────────────── */}
      <div className={styles.tableWrapper}>
        <table className={styles.table} aria-label={`Cost table: ${table.title}`}>
          <thead>
            <tr>
              <th className={styles.th}>Service</th>
              <th className={styles.th}>Provider</th>
              <th className={`${styles.th} ${styles.thNumeric}`}>Base Price</th>
              <th className={`${styles.th} ${styles.thNumeric}`}>Margin %</th>
              <th className={`${styles.th} ${styles.thNumeric}`}>Client Price</th>
              <th className={`${styles.th} ${styles.thAction}`} aria-label="Actions" />
            </tr>
          </thead>

          <tbody>
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.emptyCell}>
                  No rows. Click "+ Add row" to get started.
                </td>
              </tr>
            ) : (
              table.rows.map((row, rowIndex) => (
                /* Using rowIndex as key is acceptable here since rows are
                   ordered and not reordered independently within the table. */
                <tr
                  key={rowIndex}
                  className={rowIndex % 2 === 0 ? styles.rowEven : styles.rowOdd}
                >
                  {/* Service */}
                  <td className={styles.td}>
                    <input
                      type="text"
                      className={styles.cellInput}
                      value={row.service}
                      onChange={(e) => handleRowChange(rowIndex, 'service', e.target.value)}
                      placeholder="Service name"
                      aria-label={`Service for row ${rowIndex + 1}`}
                    />
                  </td>

                  {/* Provider — dropdown if providers list given, else text */}
                  <td className={styles.td}>
                    {providers && providers.length > 0 ? (
                      <select
                        className={styles.cellSelect}
                        value={row.provider}
                        onChange={(e) => handleRowChange(rowIndex, 'provider', e.target.value)}
                        aria-label={`Provider for row ${rowIndex + 1}`}
                      >
                        <option value="">— Select —</option>
                        {providers.map((p) => (
                          <option key={p.id} value={p.name}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className={styles.cellInput}
                        value={row.provider}
                        onChange={(e) => handleRowChange(rowIndex, 'provider', e.target.value)}
                        placeholder="Provider"
                        aria-label={`Provider for row ${rowIndex + 1}`}
                      />
                    )}
                  </td>

                  {/* Base Price */}
                  <td className={styles.td}>
                    <input
                      type="number"
                      className={`${styles.cellInput} ${styles.cellInputNumeric}`}
                      value={row.basePrice}
                      min={0}
                      step={0.01}
                      onChange={(e) => handleRowChange(rowIndex, 'basePrice', e.target.value)}
                      aria-label={`Base price for row ${rowIndex + 1}`}
                    />
                  </td>

                  {/* Margin % */}
                  <td className={styles.td}>
                    <input
                      type="number"
                      className={`${styles.cellInput} ${styles.cellInputNumeric}`}
                      value={row.marginPercent}
                      min={0}
                      max={100}
                      step={0.5}
                      onChange={(e) =>
                        handleRowChange(rowIndex, 'marginPercent', e.target.value)
                      }
                      aria-label={`Margin percentage for row ${rowIndex + 1}`}
                    />
                  </td>

                  {/* Client Price — read-only, auto-calculated */}
                  <td className={`${styles.td} ${styles.tdClientPrice}`}>
                    <span
                      className={styles.clientPriceValue}
                      aria-label={`Client price for row ${rowIndex + 1}: ${formatCurrency(row.clientPrice)}`}
                    >
                      {formatCurrency(row.clientPrice)}
                    </span>
                  </td>

                  {/* Delete row */}
                  <td className={`${styles.td} ${styles.tdAction}`}>
                    <button
                      type="button"
                      className={styles.deleteRowButton}
                      onClick={() => handleDeleteRow(rowIndex)}
                      aria-label={`Delete row ${rowIndex + 1}`}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>

          {/* Total row */}
          <tfoot>
            <tr className={styles.totalRow}>
              <td className={styles.totalLabelCell} colSpan={4}>
                Total
              </td>
              <td className={`${styles.td} ${styles.totalValueCell}`}>
                {formatCurrency(table.total)}
              </td>
              <td className={styles.td} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Add row button ──────────────────────────────────────────────── */}
      <button
        type="button"
        className={styles.addRowButton}
        onClick={handleAddRow}
        aria-label="Add new row to table"
      >
        + Add row
      </button>
    </div>
  );
};
